import { useState, useEffect } from 'react';
import { ProgramApiService, NextWorkout, ExerciseResult } from '../services/api/ProgramApiService';
import { getDateKey } from '../utils';

const STORAGE_KEY = 'activeWorkout';
const SYNC_EVENT = 'activeWorkoutChanged';

interface ActiveWorkoutSet {
  set_index: number;
  reps_completed: number;
  weight_kg: number;
  completed: boolean;
}

interface ActiveWorkoutExercise {
  program_exercise_id: number;
  name: string;
  sets: number;
  reps: number;
  current_weight_kg: number;
  consecutive_failures: number;
  order_in_template: number;
  sets_data: ActiveWorkoutSet[];
}

export interface ActiveWorkoutState {
  enrollmentId: number;
  templateId: number;
  templateName: string;
  startedAt: string;
  exercises: ActiveWorkoutExercise[];
  notes: string;
}

function readFromStorage(): ActiveWorkoutState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as ActiveWorkoutState) : null;
  } catch {
    return null;
  }
}

function writeToStorage(state: ActiveWorkoutState | null): void {
  if (state) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } else {
    localStorage.removeItem(STORAGE_KEY);
  }
}

export function useActiveWorkout() {
  const [activeWorkout, setActiveWorkout] = useState<ActiveWorkoutState | null>(readFromStorage);

  // Keep in sync across multiple hook instances in the same page
  useEffect(() => {
    const handler = () => setActiveWorkout(readFromStorage());
    window.addEventListener(SYNC_EVENT, handler);
    return () => window.removeEventListener(SYNC_EVENT, handler);
  }, []);

  const persist = (state: ActiveWorkoutState | null) => {
    writeToStorage(state);
    setActiveWorkout(state);
    window.dispatchEvent(new CustomEvent(SYNC_EVENT));
  };

  const startWorkout = (enrollmentId: number, nextWorkout: NextWorkout) => {
    const state: ActiveWorkoutState = {
      enrollmentId,
      templateId: nextWorkout.template_id,
      templateName: nextWorkout.template_name,
      startedAt: new Date().toISOString(),
      exercises: nextWorkout.exercises.map((ex) => ({
        program_exercise_id: ex.program_exercise_id,
        name: ex.name,
        sets: ex.sets,
        reps: ex.reps,
        current_weight_kg: ex.current_weight_kg,
        consecutive_failures: ex.consecutive_failures,
        order_in_template: ex.order_in_template,
        sets_data: Array.from({ length: ex.sets }, (_, i) => ({
          set_index: i,
          reps_completed: ex.reps,
          weight_kg: ex.current_weight_kg,
          completed: false,
        })),
      })),
      notes: '',
    };
    persist(state);
  };

  const markSetDone = (programExerciseId: number, setIndex: number) => {
    if (!activeWorkout) return;
    const updated: ActiveWorkoutState = {
      ...activeWorkout,
      exercises: activeWorkout.exercises.map((ex) => {
        if (ex.program_exercise_id !== programExerciseId) return ex;
        return {
          ...ex,
          sets_data: ex.sets_data.map((s) =>
            s.set_index === setIndex ? { ...s, completed: !s.completed } : s
          ),
        };
      }),
    };
    persist(updated);
  };

  const updateSet = (
    programExerciseId: number,
    setIndex: number,
    reps: number,
    weight: number
  ) => {
    if (!activeWorkout) return;
    const updated: ActiveWorkoutState = {
      ...activeWorkout,
      exercises: activeWorkout.exercises.map((ex) => {
        if (ex.program_exercise_id !== programExerciseId) return ex;
        return {
          ...ex,
          sets_data: ex.sets_data.map((s) =>
            s.set_index === setIndex
              ? { ...s, reps_completed: reps, weight_kg: weight }
              : s
          ),
        };
      }),
    };
    persist(updated);
  };

  const updateNotes = (notes: string) => {
    if (!activeWorkout) return;
    persist({ ...activeWorkout, notes });
  };

  const clearWorkout = () => persist(null);

  const finishWorkout = async () => {
    if (!activeWorkout) return;

    const exercise_results: ExerciseResult[] = activeWorkout.exercises.map((ex) => {
      const completed = ex.sets_data.filter((s) => s.completed);
      const lastCompleted = completed[completed.length - 1];
      return {
        program_exercise_id: ex.program_exercise_id,
        sets_completed: completed.length,
        reps_completed: completed.map((s) => s.reps_completed),
        weight_used_kg: lastCompleted ? lastCompleted.weight_kg : ex.current_weight_kg,
      };
    });

    await ProgramApiService.logSession(activeWorkout.enrollmentId, {
      date: getDateKey(new Date(activeWorkout.startedAt)),
      template_id: activeWorkout.templateId,
      notes: activeWorkout.notes.trim() || undefined,
      exercise_results,
    });

    clearWorkout();
  };

  const isInProgress = activeWorkout !== null;

  return {
    activeWorkout,
    isInProgress,
    startWorkout,
    markSetDone,
    updateSet,
    updateNotes,
    finishWorkout,
    clearWorkout,
  };
}
